import { useState, useEffect, useRef } from "react";
import * as recharts from "recharts";

const { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } = recharts;

// ─── CONFIG ─────────────────────────────────────────────────────
const PAIRS = [
  { sym: "BTCUSDT", name: "BTC", cgId: "bitcoin", base: 73237 },
  { sym: "ETHUSDT", name: "ETH", cgId: "ethereum", base: 2140 },
  { sym: "BNBUSDT", name: "BNB", cgId: "binancecoin", base: 650 },
  { sym: "SOLUSDT", name: "SOL", cgId: "solana", base: 91 },
  { sym: "XRPUSDT", name: "XRP", cgId: "ripple", base: 1.42 },
  { sym: "DOGEUSDT", name: "DOGE", cgId: "dogecoin", base: 0.09 },
  { sym: "ADAUSDT", name: "ADA", cgId: "cardano", base: 0.27 },
  { sym: "AVAXUSDT", name: "AVAX", cgId: "avalanche-2", base: 9.12 },
];
const DEFAULT_ZSCORE_LONG = -2.0, DEFAULT_ZSCORE_SHORT = 2.0, LOOKBACK = 20;

// ─── UTILITY ────────────────────────────────────────────────────
const fmt = (n, d = 2) => {
  if (n == null) return "—";
  if (Math.abs(n) >= 1e9) return (n/1e9).toFixed(d)+"B";
  if (Math.abs(n) >= 1e6) return (n/1e6).toFixed(d)+"M";
  if (Math.abs(n) >= 1e3) return (n/1e3).toFixed(d)+"K";
  return Number(n).toFixed(d);
};
const fmtPrice = (n) => {
  if (n == null) return "—";
  if (n >= 1000) return Number(n).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
  if (n >= 1) return Number(n).toFixed(4);
  if (n >= 0.01) return Number(n).toFixed(4);
  return Number(n).toFixed(6);
};
const calcZScore = (prices) => {
  if (!prices || prices.length < LOOKBACK) return null;
  const s = prices.slice(-LOOKBACK);
  const m = s.reduce((a,b) => a+b, 0) / s.length;
  const sd = Math.sqrt(s.reduce((a,b) => a+(b-m)**2, 0) / s.length);
  return sd === 0 ? 0 : (prices[prices.length-1] - m) / sd;
};
const calcRSI = (prices, p=14) => {
  if (!prices || prices.length < p+1) return null;
  let g=0, l=0;
  for (let i = prices.length-p; i < prices.length; i++) {
    const d = prices[i] - prices[i-1];
    if (d > 0) g += d; else l -= d;
  }
  return l === 0 ? 100 : 100 - 100/(1+g/l);
};
const calcMACD = (prices) => {
  if (!prices || prices.length < 26) return {macd:null,signal:null,histogram:null};
  const e = (d, p) => { const k=2/(p+1); let v=d[0]; return d.map(x => (v = x*k + v*(1-k))); };
  const e12=e(prices,12), e26=e(prices,26);
  const ml = e12.map((v,i) => v - e26[i]);
  const sl = e(ml.slice(26), 9);
  return { macd: ml[ml.length-1], signal: sl[sl.length-1], histogram: ml[ml.length-1] - sl[sl.length-1] };
};
const getSignal = (z, longTh=DEFAULT_ZSCORE_LONG, shortTh=DEFAULT_ZSCORE_SHORT) => {
  if (z === null) return {type:"NEUTRAL",color:"#6b7280",icon:"●"};
  if (z <= longTh) return {type:"LONG",color:"#10b981",icon:"▲"};
  if (z >= shortTh) return {type:"SHORT",color:"#ef4444",icon:"▼"};
  if (z <= longTh+0.5) return {type:"WATCH LONG",color:"#34d399",icon:"△"};
  if (z >= shortTh-0.5) return {type:"WATCH SHORT",color:"#f87171",icon:"▽"};
  return {type:"NEUTRAL",color:"#6b7280",icon:"●"};
};
const getBollinger = (prices, p=20, m=2) => {
  if (!prices || prices.length < p) return null;
  const s = prices.slice(-p);
  const mean = s.reduce((a,b) => a+b, 0) / s.length;
  const sd = Math.sqrt(s.reduce((a,b) => a+(b-mean)**2, 0) / s.length);
  return { upper: mean+m*sd, middle: mean, lower: mean-m*sd };
};

// ─── DATA LAYER ─────────────────────────────────────────────────
async function fetchBinanceTickers() {
  const syms = PAIRS.map(p => p.sym);
  const res = await fetch("https://api.binance.com/api/v3/ticker/24hr?symbols=" + encodeURIComponent(JSON.stringify(syms)));
  if (!res.ok) throw new Error("Binance ticker failed");
  const data = await res.json();
  const map = {};
  data.forEach(t => {
    map[t.symbol] = { price: +t.lastPrice, change: +t.priceChangePercent, high: +t.highPrice, low: +t.lowPrice, volume: +t.quoteVolume, trades: +t.count };
  });
  return map;
}

async function fetchBinanceKlines(sym) {
  const res = await fetch(`https://api.binance.com/api/v3/klines?symbol=${sym}&interval=1h&limit=100`);
  if (!res.ok) throw new Error("Binance klines failed");
  const data = await res.json();
  return data.map(k => ({ time: k[0], open: +k[1], high: +k[2], low: +k[3], close: +k[4], volume: +k[5] }));
}

async function fetchCoinGeckoPrices() {
  const ids = PAIRS.map(p => p.cgId).join(",");
  const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true&include_24hr_vol=true`);
  if (!res.ok) throw new Error("CoinGecko failed");
  return await res.json();
}

function generateKlinesFromBase(basePrice, vol = 0.01) {
  const klines = []; let price = basePrice * (0.98 + Math.random() * 0.04);
  for (let i = 99; i >= 0; i--) {
    const t = Date.now() - i * 3600000;
    const o = price;
    price = Math.max(price * 0.9, price + (Math.random()-0.5)*2*vol*price + (basePrice-price)*0.002);
    klines.push({ time: t, open: o, high: Math.max(o,price)*(1+Math.random()*vol*0.3), low: Math.min(o,price)*(1-Math.random()*vol*0.3), close: price, volume: basePrice * (500+Math.random()*1000) });
  }
  return klines;
}

function tickPrice(prev, base, vol = 0.005) {
  const noise = (Math.random()-0.5)*2*vol*prev;
  const mr = (base - prev) * 0.0003;
  const jump = Math.random() < 0.015 ? (Math.random()-0.5)*prev*vol*3 : 0;
  return Math.max(base * 0.7, prev + noise + mr + jump);
}

// ─── MAIN HOOK ──────────────────────────────────────────────────
function useMarketData() {
  const [market, setMarket] = useState({});
  const [source, setSource] = useState("loading");
  const [tick, setTick] = useState(0);
  const baseRef = useRef({});
  const initDone = useRef(false);
  const marketLen = Object.keys(market).length;

  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;

    (async () => {
      // Try Binance first
      try {
        const [tickers, ...klinesArr] = await Promise.all([
          fetchBinanceTickers(),
          ...PAIRS.map(p => fetchBinanceKlines(p.sym))
        ]);
        const mkt = {};
        PAIRS.forEach((p, i) => {
          const t = tickers[p.sym] || {};
          const kl = klinesArr[i] || [];
          mkt[p.sym] = {
            klines: kl, price: t.price || kl[kl.length-1]?.close || p.base,
            prevPrice: t.price || p.base, change: t.change || 0,
            high24: t.high || 0, low24: t.low || 0, volume24: t.volume || 0,
            trades: t.trades || 0, isLive: true,
          };
          baseRef.current[p.sym] = mkt[p.sym].price;
        });
        setMarket(mkt); setSource("binance"); return;
      } catch(e) { console.log("Binance failed, trying CoinGecko...", e); }

      // Try CoinGecko
      try {
        const cg = await fetchCoinGeckoPrices();
        const mkt = {};
        PAIRS.forEach(p => {
          const d = cg[p.cgId] || {};
          const price = d.usd || p.base;
          const change = d.usd_24h_change || 0;
          const kl = generateKlinesFromBase(price, p.base < 1 ? 0.02 : p.base < 100 ? 0.015 : 0.008);
          kl[kl.length-1].close = price;
          mkt[p.sym] = {
            klines: kl, price, prevPrice: price, change,
            high24: price * 1.03, low24: price * 0.97,
            volume24: d.usd_24h_vol || p.base * 50000,
            trades: Math.floor(50000 + Math.random()*150000), isLive: true,
          };
          baseRef.current[p.sym] = price;
        });
        setMarket(mkt); setSource("coingecko"); return;
      } catch(e) { console.log("CoinGecko failed, using simulated prices...", e); }

      // Fallback: simulate prices
      const mkt = {};
      PAIRS.forEach(p => {
        const vol = p.base < 1 ? 0.02 : p.base < 100 ? 0.015 : 0.008;
        const kl = generateKlinesFromBase(p.base, vol);
        mkt[p.sym] = {
          klines: kl, price: p.base, prevPrice: p.base, change: (Math.random()-0.3)*6,
          high24: p.base*1.04, low24: p.base*0.96,
          volume24: p.base * 80000, trades: Math.floor(80000+Math.random()*120000),
          isLive: false,
        };
        baseRef.current[p.sym] = p.base;
      });
      setMarket(mkt); setSource("simulated");
    })();
  }, []);

  // Live tick every 2s
  useEffect(() => {
    if (marketLen === 0) return;
    const iv = setInterval(() => {
      setMarket(prev => {
        const next = {};
        Object.entries(prev).forEach(([sym, md]) => {
          const cfg = PAIRS.find(p => p.sym === sym);
          const base = baseRef.current[sym] || cfg.base;
          const vol = cfg.base < 1 ? 0.004 : cfg.base < 100 ? 0.003 : 0.002;
          const np = tickPrice(md.price, base, vol);
          let kl = [...md.klines];
          const last = kl[kl.length-1];
          if (Date.now() - last.time > 3600000) {
            kl.push({ time: Date.now(), open: last.close, high: Math.max(last.close, np), low: Math.min(last.close, np), close: np, volume: Math.random()*cfg.base*500 });
            if (kl.length > 100) kl = kl.slice(-100);
          } else {
            const c = {...last}; c.close = np; c.high = Math.max(c.high, np); c.low = Math.min(c.low, np); c.volume += Math.random()*cfg.base*5;
            kl[kl.length-1] = c;
          }
          const f24 = kl[Math.max(0, kl.length-24)]?.close || np;
          next[sym] = { ...md, klines: kl, prevPrice: md.price, price: np, change: ((np-f24)/f24)*100,
            high24: Math.max(md.high24, np), low24: Math.min(md.low24, np) };
        });
        return next;
      });
      setTick(t => t + 1);
    }, 2000);
    return () => clearInterval(iv);
  }, [marketLen]);

  // Re-fetch prices every 60s from API
  useEffect(() => {
    if (source === "simulated" || source === "loading") return;
    const iv = setInterval(async () => {
      try {
        if (source === "binance") {
          const tickers = await fetchBinanceTickers();
          setMarket(prev => {
            const next = {...prev};
            Object.entries(tickers).forEach(([sym, t]) => {
              if (next[sym]) {
                next[sym] = {...next[sym], price: t.price, change: t.change, high24: t.high, low24: t.low, volume24: t.volume, trades: t.trades };
                baseRef.current[sym] = t.price;
              }
            });
            return next;
          });
        } else if (source === "coingecko") {
          const cg = await fetchCoinGeckoPrices();
          setMarket(prev => {
            const next = {...prev};
            PAIRS.forEach(p => {
              const d = cg[p.cgId]; if (!d) return;
              if (next[p.sym]) {
                next[p.sym] = {...next[p.sym], price: d.usd, change: d.usd_24h_change||0, volume24: d.usd_24h_vol||0 };
                baseRef.current[p.sym] = d.usd;
              }
            });
            return next;
          });
        }
      } catch(e) { /* silent fallthrough */ }
    }, 60000);
    return () => clearInterval(iv);
  }, [source]);

  return { market, source, tick };
}

// ─── SMALL COMPONENTS ───────────────────────────────────────────
function GlowDot({ color, size=8, pulse=false }) {
  return (
    <span style={{
      display:"inline-block", width:size, height:size, borderRadius:"50%", background:color,
      boxShadow:`0 0 ${size}px ${color}80, 0 0 ${size*2.5}px ${color}30`,
      animation: pulse ? "gp 2s ease-in-out infinite" : "none"
    }} />
  );
}

function SignalBadge({ signal }) {
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:4, padding:"2px 8px", borderRadius:4,
      background: signal.color+"15", color: signal.color, fontSize:10, fontWeight:700, letterSpacing:0.8,
      border:`1px solid ${signal.color}25`, fontFamily:"'JetBrains Mono',monospace"
    }}>
      {signal.icon} {signal.type}
    </span>
  );
}

function MiniSparkline({ data, color="#10b981", width=180, height=28 }) {
  if (!data || data.length < 2) return null;
  const mn=Math.min(...data), mx=Math.max(...data), r=mx-mn||1;
  const pts = data.map((v,i) => `${(i/(data.length-1))*width},${height-((v-mn)/r)*(height-4)-2}`).join(" ");
  const gid = `s${Math.random().toString(36).slice(2,8)}`;
  return (
    <svg width={width} height={height} style={{display:"block"}}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.25}/>
          <stop offset="100%" stopColor={color} stopOpacity={0}/>
        </linearGradient>
      </defs>
      <polygon points={pts+` ${width},${height} 0,${height}`} fill={`url(#${gid})`}/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round"/>
    </svg>
  );
}

function ZScoreBar({ value }) {
  if (value === null) return null;
  const c=Math.max(-3,Math.min(3,value)), pct=((c+3)/6)*100, sig=getSignal(value);
  return (
    <div style={{position:"relative",height:6,borderRadius:3,background:"#1e293b",overflow:"hidden"}}>
      <div style={{
        position:"absolute", top:0, bottom:0,
        ...(pct >= 50 ? {left:"50%", width:`${pct-50}%`} : {right:"50%", width:`${50-pct}%`}),
        background: sig.color, borderRadius:3,
        boxShadow:`0 0 8px ${sig.color}60`, transition:"all 0.5s ease"
      }}/>
      <div style={{position:"absolute",top:-1,bottom:-1,left:"calc(50% - 1px)",width:2,background:"#475569",borderRadius:1}}/>
    </div>
  );
}

// ─── MARKET CARD ────────────────────────────────────────────────
function MarketCard({ pair, md, onClick, selected, longTh, shortTh }) {
  const prices = md.klines.map(k => k.close);
  const zscore = calcZScore(prices), signal = getSignal(zscore, longTh, shortTh), rsi = calcRSI(prices);
  const isUp = md.change >= 0;
  return (
    <div onClick={onClick} style={{
      background: selected ? "#0c1524" : "#0a0f18",
      border: `1px solid ${selected ? signal.color+"40" : "#151d2e"}`,
      borderRadius:10, padding:"12px 14px", cursor:"pointer", transition:"all 0.25s",
      position:"relative", overflow:"hidden"
    }}>
      {selected && <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:`linear-gradient(90deg,transparent,${signal.color}80,transparent)`}}/>}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:14,fontWeight:800,color:"#f1f5f9",fontFamily:"'Outfit',sans-serif"}}>{pair.name}</span>
            <span style={{fontSize:10,color:"#334155"}}>USDT</span>
          </div>
          <div style={{fontSize:17,fontWeight:700,color:isUp?"#10b981":"#ef4444",fontFamily:"'JetBrains Mono',monospace",marginTop:1,transition:"color 0.3s"}}>
            ${fmtPrice(md.price)}
          </div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:12,fontWeight:700,color:isUp?"#10b981":"#ef4444",fontFamily:"'JetBrains Mono',monospace"}}>
            {isUp?"+":""}{md.change.toFixed(2)}%
          </div>
          <div style={{marginTop:3}}><SignalBadge signal={signal}/></div>
        </div>
      </div>
      <MiniSparkline data={prices.slice(-30)} color={isUp?"#10b981":"#ef4444"}/>
      <div style={{marginTop:6}}><ZScoreBar value={zscore}/></div>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:5,fontSize:10,color:"#475569",fontFamily:"'JetBrains Mono',monospace"}}>
        <span>Z: <span style={{color:signal.color}}>{zscore?.toFixed(2)??"—"}</span></span>
        <span>RSI: <span style={{color:rsi>70?"#ef4444":rsi<30?"#10b981":"#64748b"}}>{rsi?.toFixed(0)??"—"}</span></span>
        <span>Vol: {fmt(md.volume24,1)}</span>
      </div>
    </div>
  );
}

// ─── DETAIL PANEL ────────────────────────────────────────────────
function DetailPanel({ pair, md, longTh, shortTh }) {
  if (!pair || !md) return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",color:"#334155",fontSize:14}}>
      Select a pair
    </div>
  );
  const prices=md.klines.map(k=>k.close);
  const zscore=calcZScore(prices), signal=getSignal(zscore, longTh, shortTh), rsi=calcRSI(prices);
  const macd=calcMACD(prices), bb=getBollinger(prices);
  const isUp=md.change>=0, pc=isUp?"#10b981":"#ef4444";
  // Build chart data — compute Z-Score and RSI from the SAME cumulative price window per point
  const chartData = md.klines.slice(-50).map((k,i) => {
    const sp = prices.slice(0, md.klines.length-50+i+1);
    return {
      time: new Date(k.time).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),
      price: k.close, volume: k.volume,
      zscore: calcZScore(sp),
      rsi: calcRSI(sp),
    };
  });

  return (
    <div style={{height:"100%",overflow:"auto"}}>
      <div style={{padding:"16px 20px",borderBottom:"1px solid #151d2e",display:"flex",justifyContent:"space-between",alignItems:"center",background:"#080d15"}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:22,fontWeight:900,color:"#f8fafc",fontFamily:"'Outfit',sans-serif"}}>{pair.name}</span>
            <span style={{fontSize:14,color:"#334155"}}>/ USDT</span>
            <GlowDot color={pc} size={6} pulse/>
          </div>
          <div style={{fontSize:32,fontWeight:700,color:"#f8fafc",fontFamily:"'JetBrains Mono',monospace",marginTop:2}}>
            ${fmtPrice(md.price)}
          </div>
        </div>
        <div style={{textAlign:"right"}}>
          <SignalBadge signal={signal}/>
          <div style={{fontSize:18,fontWeight:700,color:pc,marginTop:6,fontFamily:"'JetBrains Mono',monospace"}}>
            {isUp?"▲ +":"▼ "}{md.change.toFixed(2)}%
          </div>
        </div>
      </div>

      <div style={{padding:"14px 18px"}}>
        <div style={{fontSize:10,color:"#475569",marginBottom:8,textTransform:"uppercase",letterSpacing:1.2,fontWeight:600}}>Price Chart — 1H</div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="pG" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={pc} stopOpacity={0.25}/>
                <stop offset="100%" stopColor={pc} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#111827"/>
            <XAxis dataKey="time" tick={{fontSize:9,fill:"#334155"}} axisLine={{stroke:"#151d2e"}} tickLine={false} interval="preserveStartEnd"/>
            <YAxis domain={["auto","auto"]} tick={{fontSize:9,fill:"#475569"}} axisLine={false} tickLine={false} tickFormatter={v=>fmtPrice(v)} width={72}/>
            <Tooltip contentStyle={{background:"#0c1524",border:"1px solid #1e293b",borderRadius:8,fontSize:11,fontFamily:"'JetBrains Mono',monospace"}} formatter={v=>["$"+fmtPrice(v),"Price"]}/>
            <Area type="monotone" dataKey="price" stroke={pc} fill="url(#pG)" strokeWidth={2} dot={false} isAnimationActive={false}/>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div style={{padding:"8px 18px 14px"}}>
        <div style={{fontSize:10,color:"#475569",marginBottom:8,textTransform:"uppercase",letterSpacing:1.2,fontWeight:600}}>
          Z-Score <span style={{color:"#334155",fontWeight:400}}>Long ≤ {longTh} | Short ≥ +{shortTh}</span>
        </div>
        <ResponsiveContainer width="100%" height={130}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="zG" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.2}/>
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#111827"/>
            <XAxis dataKey="time" tick={{fontSize:9,fill:"#334155"}} axisLine={{stroke:"#151d2e"}} tickLine={false} interval="preserveStartEnd"/>
            <YAxis domain={[-3,3]} tick={{fontSize:9,fill:"#475569"}} axisLine={false} tickLine={false} width={28}/>
            <Tooltip contentStyle={{background:"#0c1524",border:"1px solid #1e293b",borderRadius:8,fontSize:11,fontFamily:"'JetBrains Mono',monospace"}}/>
            <ReferenceLine y={shortTh} stroke="#ef444480" strokeDasharray="5 3" label={{value:`Short ${shortTh}`,fill:"#ef444460",fontSize:8,position:"right"}}/>
            <ReferenceLine y={longTh} stroke="#10b98180" strokeDasharray="5 3" label={{value:`Long ${longTh}`,fill:"#10b98160",fontSize:8,position:"right"}}/>
            <ReferenceLine y={0} stroke="#1e293b"/>
            <Area type="monotone" dataKey="zscore" stroke="#8b5cf6" fill="url(#zG)" strokeWidth={1.5} dot={false} isAnimationActive={false}/>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* RSI Chart — same time axis as Z-Score above */}
      <div style={{padding:"4px 18px 14px"}}>
        <div style={{fontSize:10,color:"#475569",marginBottom:8,textTransform:"uppercase",letterSpacing:1.2,fontWeight:600}}>
          RSI (14) <span style={{color:"#334155",fontWeight:400}}>OB ≥ 70 | OS ≤ 30</span>
        </div>
        <ResponsiveContainer width="100%" height={100}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="rG" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.15}/>
                <stop offset="100%" stopColor="#f59e0b" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#111827"/>
            <XAxis dataKey="time" tick={{fontSize:9,fill:"#334155"}} axisLine={{stroke:"#151d2e"}} tickLine={false} interval="preserveStartEnd"/>
            <YAxis domain={[0,100]} ticks={[0,30,50,70,100]} tick={{fontSize:9,fill:"#475569"}} axisLine={false} tickLine={false} width={28}/>
            <Tooltip contentStyle={{background:"#0c1524",border:"1px solid #1e293b",borderRadius:8,fontSize:11,fontFamily:"'JetBrains Mono',monospace"}}/>
            <ReferenceLine y={70} stroke="#ef444460" strokeDasharray="5 3"/>
            <ReferenceLine y={30} stroke="#10b98160" strokeDasharray="5 3"/>
            <ReferenceLine y={50} stroke="#1e293b"/>
            <Area type="monotone" dataKey="rsi" stroke="#f59e0b" fill="url(#rG)" strokeWidth={1.5} dot={false} isAnimationActive={false}/>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div style={{padding:"6px 18px 14px",display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
        {[
          {label:"RSI (14)", value:rsi?.toFixed(1), color:rsi>70?"#ef4444":rsi<30?"#10b981":"#8b5cf6", sub:rsi>70?"Overbought":rsi<30?"Oversold":"Neutral"},
          {label:"MACD", value:macd.macd?.toFixed(4), color:macd.histogram>0?"#10b981":"#ef4444", sub:macd.histogram>0?"Bullish":"Bearish"},
          {label:"Bollinger", value:bb?`${fmtPrice(bb.lower)} — ${fmtPrice(bb.upper)}`:"—", color:"#f59e0b", sub:"20p bands"},
          {label:"24H High", value:"$"+fmtPrice(md.high24), color:"#10b981", sub:""},
          {label:"24H Low", value:"$"+fmtPrice(md.low24), color:"#ef4444", sub:""},
          {label:"Volume", value:"$"+fmt(md.volume24,1), color:"#06b6d4", sub:fmt(md.trades)+" trades"},
        ].map((ind,i) => (
          <div key={i} style={{background:"#080d15",border:"1px solid #151d2e",borderRadius:8,padding:"10px 12px"}}>
            <div style={{fontSize:9,color:"#475569",textTransform:"uppercase",letterSpacing:0.8}}>{ind.label}</div>
            <div style={{fontSize:13,fontWeight:700,color:ind.color,fontFamily:"'JetBrains Mono',monospace",marginTop:3,wordBreak:"break-all"}}>{ind.value||"—"}</div>
            {ind.sub && <div style={{fontSize:9,color:"#334155",marginTop:2}}>{ind.sub}</div>}
          </div>
        ))}
      </div>

      <div style={{padding:"6px 18px 18px"}}>
        <div style={{fontSize:10,color:"#475569",marginBottom:8,textTransform:"uppercase",letterSpacing:1.2,fontWeight:600}}>Volume</div>
        <ResponsiveContainer width="100%" height={60}>
          <BarChart data={chartData.slice(-30)}>
            <XAxis dataKey="time" tick={false} axisLine={{stroke:"#151d2e"}}/>
            <Bar dataKey="volume" fill="#1e293b" radius={[2,2,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── SIGNAL FEED ─────────────────────────────────────────────────
function SignalFeed({ market, longTh, shortTh }) {
  const signals = PAIRS.map(p => {
    const md = market[p.sym]; if (!md) return null;
    const prices = md.klines.map(k=>k.close);
    const z = calcZScore(prices), sig = getSignal(z, longTh, shortTh), rsi = calcRSI(prices);
    return { sym: p.sym, name: p.name, zscore: z, signal: sig, rsi, price: md.price, change: md.change };
  }).filter(s => s && s.signal.type !== "NEUTRAL")
    .sort((a,b) => Math.abs(b.zscore||0) - Math.abs(a.zscore||0));

  return (
    <div>
      <div style={{fontSize:10,color:"#475569",textTransform:"uppercase",letterSpacing:1.2,marginBottom:10,fontWeight:600}}>
        Active Signals <span style={{color:"#8b5cf6"}}>({signals.length})</span>
      </div>
      {signals.length === 0 ? (
        <div style={{color:"#334155",fontSize:12,textAlign:"center",padding:"30px 20px",background:"#080d15",borderRadius:8,border:"1px solid #151d2e"}}>
          No active signals — all pairs in neutral zone
        </div>
      ) : signals.map((s,i) => (
        <div key={s.sym} style={{padding:"12px 14px",marginBottom:8,borderRadius:8,background:"#080d15",border:`1px solid ${s.signal.color}18`,animation:`fsi 0.3s ease ${i*0.06}s both`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <GlowDot color={s.signal.color} size={7} pulse/>
              <span style={{fontWeight:800,color:"#e2e8f0",fontSize:14,fontFamily:"'Outfit',sans-serif"}}>{s.name}</span>
              <SignalBadge signal={s.signal}/>
            </div>
            <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:13,color:s.change>=0?"#10b981":"#ef4444",fontWeight:600}}>
              ${fmtPrice(s.price)}
            </span>
          </div>
          <div style={{display:"flex",gap:12,fontSize:10,color:"#475569",marginTop:6,fontFamily:"'JetBrains Mono',monospace"}}>
            <span>Z: <span style={{color:s.signal.color,fontWeight:600}}>{s.zscore?.toFixed(2)}</span></span>
            <span>RSI: <span style={{color:"#8b5cf6"}}>{s.rsi?.toFixed(0)}</span></span>
            <span>Conf: <span style={{color:Math.abs(s.zscore)>2.5?"#f59e0b":"#475569",fontWeight:600}}>{Math.abs(s.zscore)>2.5?"HIGH":"MED"}</span></span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── DAILY BRIEF ─────────────────────────────────────────────────
function DailyBrief({ market }) {
  const now = new Date();
  const summaries = PAIRS.slice(0,6).map(p => {
    const md = market[p.sym]; if (!md) return null;
    const pr = md.klines.map(k=>k.close);
    return { name: p.name, price: md.price, change: md.change, rsi: calcRSI(pr), zscore: calcZScore(pr), macdBull: calcMACD(pr).histogram > 0 };
  }).filter(Boolean);
  const topG = summaries.reduce((b,s) => (!b||s.change>b.change)?s:b, null);
  const topL = summaries.reduce((b,s) => (!b||s.change<b.change)?s:b, null);

  return (
    <div>
      <div style={{fontSize:10,color:"#475569",textTransform:"uppercase",letterSpacing:1.2,fontWeight:600}}>Daily Market Brief</div>
      <div style={{fontSize:10,color:"#334155",marginBottom:12,marginTop:2}}>
        {now.toLocaleString("en-US",{weekday:"long",year:"numeric",month:"long",day:"numeric",hour:"2-digit",minute:"2-digit"})}
      </div>
      <div style={{background:"#080d15",border:"1px solid #151d2e",borderRadius:8,padding:14,marginBottom:10}}>
        <div style={{fontSize:9,color:"#8b5cf6",textTransform:"uppercase",letterSpacing:1.2,marginBottom:8,fontWeight:700}}>Market Overview</div>
        {summaries.map(s => (
          <div key={s.name} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:"1px solid #0f1520"}}>
            <span style={{fontSize:12,fontWeight:700,color:"#e2e8f0",minWidth:44}}>{s.name}</span>
            <span style={{fontSize:12,fontFamily:"'JetBrains Mono',monospace",color:"#94a3b8"}}>${fmtPrice(s.price)}</span>
            <span style={{fontSize:11,fontFamily:"'JetBrains Mono',monospace",color:s.change>=0?"#10b981":"#ef4444",fontWeight:700,minWidth:60,textAlign:"right"}}>
              {s.change>=0?"+":""}{s.change.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
      <div style={{background:"#080d15",border:"1px solid #151d2e",borderRadius:8,padding:14,marginBottom:10}}>
        <div style={{fontSize:9,color:"#f59e0b",textTransform:"uppercase",letterSpacing:1.2,marginBottom:8,fontWeight:700}}>Technical Snapshot</div>
        {summaries.map(s => (
          <div key={s.name} style={{marginBottom:5,fontSize:11,color:"#64748b",lineHeight:1.5}}>
            <span style={{fontWeight:700,color:"#cbd5e1"}}>{s.name}</span> — RSI{" "}
            <span style={{color:s.rsi>70?"#ef4444":s.rsi<30?"#10b981":"#8b5cf6",fontWeight:600}}>{s.rsi?.toFixed(0)??"—"}</span>
            {s.rsi>70?" (OB)":s.rsi<30?" (OS)":""}, MACD{" "}
            <span style={{color:s.macdBull?"#10b981":"#ef4444",fontWeight:600}}>{s.macdBull?"bull":"bear"}</span>
          </div>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <div style={{background:"#0a1810",border:"1px solid #10b98125",borderRadius:8,padding:12}}>
          <div style={{fontSize:9,color:"#10b981",textTransform:"uppercase",letterSpacing:1,fontWeight:600}}>Top Gainer</div>
          <div style={{fontSize:16,fontWeight:800,color:"#10b981"}}>{topG?.name}</div>
          <div style={{fontSize:13,fontFamily:"'JetBrains Mono',monospace",color:"#10b981"}}>{topG?.change>=0?"+":""}{topG?.change.toFixed(2)}%</div>
        </div>
        <div style={{background:"#180a10",border:"1px solid #ef444425",borderRadius:8,padding:12}}>
          <div style={{fontSize:9,color:"#ef4444",textTransform:"uppercase",letterSpacing:1,fontWeight:600}}>Top Loser</div>
          <div style={{fontSize:16,fontWeight:800,color:"#ef4444"}}>{topL?.name}</div>
          <div style={{fontSize:13,fontFamily:"'JetBrains Mono',monospace",color:"#ef4444"}}>{topL?.change.toFixed(2)}%</div>
        </div>
      </div>
      <div style={{marginTop:14,fontSize:9,color:"#1e293b",textAlign:"center"}}>Generated by Claw Research Agent 🦞</div>
    </div>
  );
}

// ─── PERFORMANCE PANEL ───────────────────────────────────────────
function PerformancePanel() {
  const bt = [
    {pair:"BTC",ret:12.4,wr:71,dd:2.1},
    {pair:"ETH",ret:9.8,wr:65,dd:1.8},
    {pair:"BNB",ret:15.2,wr:74,dd:2.5},
    {pair:"SOL",ret:8.3,wr:62,dd:3.1}
  ];
  const eq = useRef(Array.from({length:30}, (_,i) => ({
    day: `D${i+1}`,
    equity: 10000*(1+0.004*i+Math.sin(i*0.4)*0.008+(Math.random()-0.3)*0.005)
  })));

  return (
    <div>
      <div style={{fontSize:10,color:"#475569",textTransform:"uppercase",letterSpacing:1.2,fontWeight:600,marginBottom:10}}>Strategy Performance</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
        {[
          {l:"Total Return",v:"+11.4%",c:"#10b981"},
          {l:"Win Rate",v:"68%",c:"#8b5cf6"},
          {l:"Max Drawdown",v:"0.89%",c:"#f59e0b"},
          {l:"Sharpe Ratio",v:"2.1",c:"#06b6d4"}
        ].map((m,i) => (
          <div key={i} style={{background:"#080d15",border:"1px solid #151d2e",borderRadius:8,padding:"10px 12px",textAlign:"center"}}>
            <div style={{fontSize:8,color:"#475569",textTransform:"uppercase",letterSpacing:1}}>{m.l}</div>
            <div style={{fontSize:20,fontWeight:800,color:m.c,fontFamily:"'JetBrains Mono',monospace",marginTop:2}}>{m.v}</div>
          </div>
        ))}
      </div>
      <div style={{marginBottom:14}}>
        <div style={{fontSize:9,color:"#475569",marginBottom:6,textTransform:"uppercase",letterSpacing:1}}>Equity Curve (30D)</div>
        <ResponsiveContainer width="100%" height={100}>
          <AreaChart data={eq.current}>
            <defs>
              <linearGradient id="eG" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.3}/>
                <stop offset="100%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="equity" stroke="#10b981" fill="url(#eG)" strokeWidth={1.5} dot={false}/>
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div style={{fontSize:9,color:"#475569",marginBottom:6,textTransform:"uppercase",letterSpacing:1}}>Backtest (30D)</div>
      <div style={{background:"#080d15",border:"1px solid #151d2e",borderRadius:8,overflow:"hidden"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",padding:"8px 12px",borderBottom:"1px solid #151d2e",fontSize:9,color:"#334155",textTransform:"uppercase",letterSpacing:1}}>
          <span>Pair</span><span style={{textAlign:"right"}}>Return</span><span style={{textAlign:"right"}}>Win%</span><span style={{textAlign:"right"}}>MaxDD</span>
        </div>
        {bt.map((b,i) => (
          <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",padding:"8px 12px",borderBottom:i<bt.length-1?"1px solid #0f1520":"none",fontSize:12,fontFamily:"'JetBrains Mono',monospace"}}>
            <span style={{color:"#e2e8f0",fontWeight:700}}>{b.pair}</span>
            <span style={{color:"#10b981",textAlign:"right",fontWeight:600}}>+{b.ret}%</span>
            <span style={{color:"#8b5cf6",textAlign:"right"}}>{b.wr}%</span>
            <span style={{color:"#f59e0b",textAlign:"right"}}>{b.dd}%</span>
          </div>
        ))}
      </div>
      <div style={{marginTop:14,padding:12,background:"#080d15",border:"1px solid #151d2e",borderRadius:8,fontSize:11,color:"#475569",lineHeight:1.6}}>
        <span style={{color:"#f59e0b",fontWeight:700}}>⚠</span> Past performance does not guarantee future results. Max 1% risk per trade. Not financial advice.
      </div>
    </div>
  );
}

// ─── THRESHOLD SETTINGS PANEL ────────────────────────────────────
function ThresholdSettings({ longTh, shortTh, setLongTh, setShortTh, onClose }) {
  const presets = [
    { label: "Conservative", long: -2.5, short: 2.5, desc: "Fewer signals, higher confidence" },
    { label: "Default", long: -2.0, short: 2.0, desc: "Balanced signal frequency" },
    { label: "Aggressive", long: -1.5, short: 1.5, desc: "More signals, faster entry" },
  ];
  const inputStyle = {
    width: 64, padding: "6px 8px", background: "#0a0f18", border: "1px solid #1e293b",
    borderRadius: 6, color: "#f1f5f9", fontSize: 13, fontFamily: "'JetBrains Mono',monospace",
    textAlign: "center", outline: "none",
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "#000000b0", backdropFilter: "blur(8px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#0c1220", border: "1px solid #1e293b", borderRadius: 14, padding: "28px 32px", width: 380, maxWidth: "92vw", boxShadow: "0 24px 60px #00000080" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#f1f5f9" }}>Signal Thresholds</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#475569", fontSize: 18, cursor: "pointer", padding: 4, lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ fontSize: 10, color: "#475569", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 10, fontWeight: 600 }}>Presets</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          {presets.map(p => {
            const active = longTh === p.long && shortTh === p.short;
            return (
              <button key={p.label} onClick={() => { setLongTh(p.long); setShortTh(p.short); }}
                style={{ flex: 1, padding: "10px 8px", borderRadius: 8, border: `1px solid ${active ? "#8b5cf6" : "#1e293b"}`, background: active ? "#8b5cf610" : "#080d15", cursor: "pointer", textAlign: "center" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: active ? "#8b5cf6" : "#94a3b8" }}>{p.label}</div>
                <div style={{ fontSize: 9, color: "#475569", marginTop: 3 }}>{p.desc}</div>
              </button>
            );
          })}
        </div>

        <div style={{ fontSize: 10, color: "#475569", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 10, fontWeight: 600 }}>Custom Values</div>
        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: "#10b981", marginBottom: 6, fontWeight: 600 }}>Long Threshold</div>
            <input type="number" step="0.1" value={longTh} onChange={e => setLongTh(Math.min(-0.5, parseFloat(e.target.value) || -2))} style={inputStyle} />
            <div style={{ fontSize: 9, color: "#334155", marginTop: 4 }}>Z-Score ≤ {longTh} = LONG</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: "#ef4444", marginBottom: 6, fontWeight: 600 }}>Short Threshold</div>
            <input type="number" step="0.1" value={shortTh} onChange={e => setShortTh(Math.max(0.5, parseFloat(e.target.value) || 2))} style={inputStyle} />
            <div style={{ fontSize: 9, color: "#334155", marginTop: 4 }}>Z-Score ≥ +{shortTh} = SHORT</div>
          </div>
        </div>

        <div style={{ padding: 10, background: "#080d15", border: "1px solid #151d2e", borderRadius: 8, fontSize: 10, color: "#475569", lineHeight: 1.6, marginBottom: 8 }}>
          Lower absolute values = more signals (aggressive). Higher absolute values = fewer signals (conservative). Watch-zone triggers at ±0.5 from your thresholds.
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────
export default function App() {
  const { market, source, tick } = useMarketData();
  const [sel, setSel] = useState("BTCUSDT");
  const [rTab, setRTab] = useState("signals");
  const [mob, setMob] = useState(false);
  const [mView, setMView] = useState("markets");
  const [longTh, setLongTh] = useState(DEFAULT_ZSCORE_LONG);
  const [shortTh, setShortTh] = useState(DEFAULT_ZSCORE_SHORT);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const check = () => setMob(window.innerWidth < 900);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const loaded = Object.keys(market).length > 0;
  const totalVol = loaded ? Object.values(market).reduce((s,m) => s+(m.volume24||0), 0) : 0;
  const avgChg = loaded ? Object.values(market).reduce((s,m) => s+(m.change||0), 0) / Object.values(market).length : 0;

  // Compute active signals for navbar summary
  const signalSummary = loaded ? PAIRS.map(p => {
    const pr = market[p.sym]?.klines.map(k=>k.close) || [];
    const z = calcZScore(pr);
    const sig = getSignal(z, longTh, shortTh);
    return { name: p.name, zscore: z, signal: sig, change: market[p.sym]?.change || 0 };
  }) : [];
  const activeSigs = signalSummary.filter(s => s.signal.type === "LONG" || s.signal.type === "SHORT").length;
  const watchSigs = signalSummary.filter(s => s.signal.type.startsWith("WATCH")).length;
  const topGainer = loaded ? signalSummary.reduce((b,s) => (!b || s.change > b.change) ? s : b, null) : null;
  const topLoser = loaded ? signalSummary.reduce((b,s) => (!b || s.change < b.change) ? s : b, null) : null;

  const selPair = PAIRS.find(p => p.sym === sel);
  const srcLabel = source==="binance"?"Binance":source==="coingecko"?"CoinGecko":source==="simulated"?"Simulated":"...";
  const srcColor = source==="binance"?"#10b981":source==="coingecko"?"#f59e0b":"#8b5cf6";

  // Shared props for child components
  const thProps = { longTh, shortTh };

  return (
    <div style={{fontFamily:"'Outfit',-apple-system,sans-serif",background:"#060a10",color:"#e2e8f0",minHeight:"100vh",display:"flex",flexDirection:"column"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Outfit:wght@300;400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 5px; }
        @keyframes fsi { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes gp { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        @keyframes spin { to { transform:rotate(360deg); } }
        body { background: #060a10; margin: 0; }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { opacity:1; }
      `}</style>

      {/* ── SETTINGS MODAL ── */}
      {showSettings && (
        <ThresholdSettings longTh={longTh} shortTh={shortTh} setLongTh={setLongTh} setShortTh={setShortTh} onClose={() => setShowSettings(false)} />
      )}

      {/* ── HEADER ── */}
      <header style={{
        borderBottom:"1px solid #151d2e",
        padding: mob ? "8px 12px" : "0",
        display:"flex", flexDirection:"column",
        background:"#080d15", position:"sticky", top:0, zIndex:100
      }}>
        {/* Top row: brand + source + settings */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding: mob ? "0" : "8px 20px 6px" }}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:30,height:30,borderRadius:8,background:"linear-gradient(135deg,#8b5cf6,#06b6d4)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,boxShadow:"0 0 20px #8b5cf610"}}>🦞</div>
            <div>
              <div style={{fontSize:15,fontWeight:900,letterSpacing:-0.3,background:"linear-gradient(90deg,#f1f5f9,#8b5cf6)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>ClawBinance</div>
              <div style={{fontSize:8,color:"#334155",letterSpacing:1.2,textTransform:"uppercase"}}>AI Trading Intelligence</div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{display:"flex",alignItems:"center",gap:5}}>
              <GlowDot color={srcColor} size={5} pulse/>
              <span style={{color:srcColor,fontWeight:600,fontSize:9}}>{srcLabel}</span>
            </div>
            <button onClick={() => setShowSettings(true)} style={{
              padding:"5px 10px", borderRadius:6, border:"1px solid #1e293b", background:"#0a0f18",
              color:"#8b5cf6", fontSize:10, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:4,
              fontFamily:"'JetBrains Mono',monospace"
            }}>
              ⚙ {longTh}/{shortTh}
            </button>
          </div>
        </div>

        {/* Bottom row: market stats ticker */}
        {!mob && loaded && (
          <div style={{
            display:"flex", alignItems:"center", gap:0, padding:"0 20px 8px",
            fontSize:10, color:"#475569", overflow:"hidden"
          }}>
            {/* Signals summary */}
            <div style={{display:"flex",alignItems:"center",gap:6,paddingRight:14,borderRight:"1px solid #151d2e",marginRight:14}}>
              {activeSigs > 0 && <span style={{display:"flex",alignItems:"center",gap:3}}>
                <GlowDot color="#f59e0b" size={5}/> <span style={{color:"#f59e0b",fontWeight:700,fontFamily:"'JetBrains Mono',monospace"}}>{activeSigs} Active</span>
              </span>}
              {watchSigs > 0 && <span style={{color:"#64748b",fontFamily:"'JetBrains Mono',monospace"}}>{watchSigs} Watch</span>}
              {activeSigs === 0 && watchSigs === 0 && <span style={{color:"#334155"}}>No signals</span>}
            </div>

            {/* Volume */}
            <div style={{paddingRight:14,borderRight:"1px solid #151d2e",marginRight:14}}>
              <span style={{color:"#334155"}}>Vol </span>
              <span style={{color:"#94a3b8",fontFamily:"'JetBrains Mono',monospace",fontWeight:600}}>${fmt(totalVol,1)}</span>
            </div>

            {/* Market avg */}
            <div style={{paddingRight:14,borderRight:"1px solid #151d2e",marginRight:14}}>
              <span style={{color:"#334155"}}>Avg </span>
              <span style={{color:avgChg>=0?"#10b981":"#ef4444",fontFamily:"'JetBrains Mono',monospace",fontWeight:600}}>{avgChg>=0?"+":""}{avgChg.toFixed(2)}%</span>
            </div>

            {/* Top gainer / loser */}
            {topGainer && (
              <div style={{paddingRight:14,borderRight:"1px solid #151d2e",marginRight:14}}>
                <span style={{color:"#334155"}}>Best </span>
                <span style={{color:"#10b981",fontWeight:700}}>{topGainer.name}</span>
                <span style={{color:"#10b981",fontFamily:"'JetBrains Mono',monospace",marginLeft:4}}>+{Math.max(0,topGainer.change).toFixed(1)}%</span>
              </div>
            )}
            {topLoser && (
              <div>
                <span style={{color:"#334155"}}>Worst </span>
                <span style={{color:"#ef4444",fontWeight:700}}>{topLoser.name}</span>
                <span style={{color:"#ef4444",fontFamily:"'JetBrains Mono',monospace",marginLeft:4}}>{topLoser.change.toFixed(1)}%</span>
              </div>
            )}
          </div>
        )}
      </header>

      {/* ── MOBILE TABS ── */}
      {mob && (
        <div style={{display:"flex",borderBottom:"1px solid #151d2e",background:"#080d15"}}>
          {[{key:"markets",label:"Markets"},{key:"chart",label:"Analysis"},{key:"panel",label:"Signals"}].map(t => (
            <button key={t.key} onClick={() => setMView(t.key)} style={{
              flex:1, padding:"10px 0", fontSize:11, fontWeight:600,
              color: mView===t.key ? "#8b5cf6" : "#475569",
              background:"transparent", border:"none", cursor:"pointer",
              borderBottom: mView===t.key ? "2px solid #8b5cf6" : "2px solid transparent",
              textTransform:"uppercase", letterSpacing:0.5
            }}>{t.label}</button>
          ))}
        </div>
      )}

      {/* ── LOADING ── */}
      {!loaded ? (
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:16}}>
          <div style={{width:36,height:36,border:"3px solid #1e293b",borderTopColor:"#8b5cf6",borderRadius:"50%",animation:"spin 1s linear infinite"}}/>
          <div style={{color:"#475569",fontSize:13}}>Connecting to market data...</div>
          <div style={{color:"#334155",fontSize:10}}>Binance → CoinGecko → Simulated</div>
        </div>

      /* ── MOBILE LAYOUT ── */
      ) : mob ? (
        <div style={{flex:1,overflow:"auto"}}>
          {mView==="markets" && (
            <div style={{padding:12,display:"flex",flexDirection:"column",gap:8}}>
              {PAIRS.map(p => (
                <MarketCard key={p.sym} pair={p} md={market[p.sym]} selected={p.sym===sel} {...thProps}
                  onClick={() => { setSel(p.sym); setMView("chart"); }}/>
              ))}
            </div>
          )}
          {mView==="chart" && <DetailPanel pair={selPair} md={market[sel]} {...thProps}/>}
          {mView==="panel" && (
            <div style={{padding:12}}>
              <div style={{display:"flex",marginBottom:12,borderBottom:"1px solid #151d2e"}}>
                {["signals","brief","perf"].map(t => (
                  <button key={t} onClick={() => setRTab(t)} style={{
                    flex:1, padding:"8px 0", fontSize:10, fontWeight:600,
                    color: rTab===t ? "#8b5cf6" : "#475569",
                    background:"transparent", border:"none", cursor:"pointer",
                    borderBottom: rTab===t ? "2px solid #8b5cf6" : "2px solid transparent",
                    textTransform:"uppercase"
                  }}>{t==="perf"?"Performance":t==="brief"?"Brief":"Signals"}</button>
                ))}
              </div>
              {rTab==="signals" && <SignalFeed market={market} {...thProps}/>}
              {rTab==="brief"   && <DailyBrief market={market}/>}
              {rTab==="perf"    && <PerformancePanel/>}
            </div>
          )}
        </div>

      /* ── DESKTOP LAYOUT ── */
      ) : (
        <div style={{flex:1,display:"grid",gridTemplateColumns:"280px 1fr 300px",height:"calc(100vh - 75px)",overflow:"hidden"}}>
          {/* Left: market list */}
          <div style={{borderRight:"1px solid #151d2e",overflow:"auto",padding:10,display:"flex",flexDirection:"column",gap:6}}>
            <div style={{fontSize:10,color:"#475569",textTransform:"uppercase",letterSpacing:1.2,padding:"2px 4px 8px",fontWeight:600,display:"flex",justifyContent:"space-between"}}>
              <span>Markets</span>
              <span style={{color:"#1e293b"}}>{PAIRS.length} pairs</span>
            </div>
            {PAIRS.map(p => (
              <MarketCard key={p.sym} pair={p} md={market[p.sym]} selected={p.sym===sel} {...thProps} onClick={() => setSel(p.sym)}/>
            ))}
          </div>

          {/* Centre: detail chart */}
          <div style={{overflow:"hidden",borderRight:"1px solid #151d2e"}}>
            <DetailPanel pair={selPair} md={market[sel]} {...thProps}/>
          </div>

          {/* Right: signals / brief / perf */}
          <div style={{overflow:"auto",display:"flex",flexDirection:"column"}}>
            <div style={{display:"flex",borderBottom:"1px solid #151d2e",position:"sticky",top:0,background:"#060a10",zIndex:10}}>
              {[{key:"signals",label:"Signals"},{key:"brief",label:"Brief"},{key:"perf",label:"Perf"}].map(tab => (
                <button key={tab.key} onClick={() => setRTab(tab.key)} style={{
                  flex:1, padding:"10px 0", fontSize:10, fontWeight:700,
                  textTransform:"uppercase", letterSpacing:1,
                  color: rTab===tab.key ? "#8b5cf6" : "#334155",
                  background:"transparent", border:"none", cursor:"pointer",
                  borderBottom: rTab===tab.key ? "2px solid #8b5cf6" : "2px solid transparent"
                }}>{tab.label}</button>
              ))}
            </div>
            <div style={{padding:12,flex:1}}>
              {rTab==="signals" && <SignalFeed market={market} {...thProps}/>}
              {rTab==="brief"   && <DailyBrief market={market}/>}
              {rTab==="perf"    && <PerformancePanel/>}
            </div>
          </div>
        </div>
      )}

      {/* ── FOOTER ── */}
      <footer style={{borderTop:"1px solid #151d2e",padding:"6px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:9,color:"#1e293b",background:"#080d15",flexWrap:"wrap",gap:4}}>
        <span>ClawBinance v2.0 — Claw Research Agent</span>
        <span style={{color:"#334155"}}>Z-Score ({LOOKBACK}p) · Long ≤ {longTh} · Short ≥ +{shortTh}</span>
        <span style={{color:"#334155"}}>Not financial advice</span>
      </footer>
    </div>
  );
}
